import { useCreateViewFromDatasetMutation } from "graphql/types/graphql";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useContext } from "react";
import { context } from "utils/Provider";

export type Dataset = {
  id: string;
  name: string;
};

export const useHandlePromote = (
  cubeId: string,
  setOpenedDatasetIds?: React.Dispatch<React.SetStateAction<string[]>>,
  setTableOrder?: React.Dispatch<React.SetStateAction<string[]>>,
  setSelectedColumns?: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        { selectedColumns: string[]; joinColumn: string | null; joinWith?: string; joinWithColumn?: string | null }
      >
    >
  >,
  setDatasetId?: React.Dispatch<React.SetStateAction<string | null>>
) => {
  const [createViewFromDataset] = useCreateViewFromDatasetMutation({
    update: (cache) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "views",
      });
    },
  });
  const { setIsLoading } = useContext(context);

  const handlePromote = async (matcherOutputName: string, datasetId: string, datasetName: string) => {
    if (!matcherOutputName.trim()) {
      enqueueSnackbar("Please specify a matcher output name.", { variant: "error" });
      return;
    }

    if (!datasetId) {
      enqueueSnackbar("Please select a dataset to promote.", { variant: "error" });
      return;
    }

    const snackbarKey = enqueueSnackbar("Promoting dataset to matcher...", {
      variant: "info",
      persist: true,
    });

    try {
      setIsLoading(true);
      const response = await createViewFromDataset({
        variables: {
          payload: {
            outputViewName: matcherOutputName,
            cubeId,
            datasetId,
            datasetName,
          },
        },
      });

      closeSnackbar(snackbarKey);

      if (response.data?.createViewFromDataset.success) {
        enqueueSnackbar(`Dataset ${datasetName} promoted successfully`, {
          variant: "success",
          autoHideDuration: 5000,
        });

        if (setOpenedDatasetIds) setOpenedDatasetIds([]);
        if (setTableOrder) setTableOrder([]);
        if (setSelectedColumns) setSelectedColumns({});
        if (setDatasetId) setDatasetId(null);
      } else {
        enqueueSnackbar(`Promotion failed: ${response.data?.createViewFromDataset.error}`, {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      closeSnackbar(snackbarKey);
      enqueueSnackbar(
        `An error occurred while promoting dataset: ${error instanceof Error ? error.message : "Something went wrong"}`,
        {
          variant: "error",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return { handlePromote };
};
